import { useCompany, useCompanyCanonicalUrl } from '@/hooks/useCompany';
import type { CompanyResponse } from '@/hooks/useCompany/types';
import { useSection } from '../useSection';
import { useLocale } from '@/hooks/useLocale';
type Company = CompanyResponse['data'];
function getDividendYield(company?: Company) {
  return company?.analysis?.data.extended.data.analysis.dividend.dividend_yield;
}

const indexConfig = {
  de: [
    'NYSE',
    'NasdaqGS',
    'NasdaqGM',
    'NasdaqCM',
    'DUSE',
    'DB',
    'MUN',
    'XTRA',
    'HMSE',
    'WBAG',
    'BRSE',
    'BST',
    'SWX',
  ],
  es: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'BME', 'BMV'],
  fr: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'COBL', 'ENXTPA'],
  it: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'BITE', 'BIT', 'XMIL'],
  nl: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'ENXTAM'],
  tr: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'IBSE'],
  pt: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'ENXTLS'],
  ja: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'TSE'],
  ko: ['NYSE', 'NasdaqGS', 'NasdaqGM', 'NasdaqCM', 'KOSDAQ', 'KOSE'],
};

function useShouldNoIndexFollow() {
  const { currentSection } = useSection();
  const canonicalUrl = useCompanyCanonicalUrl();
  const company = useCompany(canonicalUrl);
  const dividendYield = getDividendYield(company.data?.data);

  const locale = useLocale();

  const isUntargetedLocaleExchangeCombination =
    locale !== 'en' &&
    !indexConfig[locale]?.includes(company.data?.data?.exchange_symbol || '');

  const primaryCanonicalUrl = company?.data?.data.primary_canonical_url;
  const isPrimaryListing =
    company?.data?.data.canonical_url === undefined ||
    company?.data.data.canonical_url === primaryCanonicalUrl;
  const userViews90d = company?.data?.data.statistics?.data?.user_views_90d;

  const isSecondaryInsufficientUserViews =
    !isPrimaryListing && userViews90d === 0;

  const isBlockedAsInsufficientDividend =
    (dividendYield === 0 || dividendYield === null) &&
    currentSection === 'dividend';

  return (
    isBlockedAsInsufficientDividend ||
    isSecondaryInsufficientUserViews ||
    isUntargetedLocaleExchangeCombination
  );
}

export default useShouldNoIndexFollow;
